import { useMemo, useRef, useCallback } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import useLearningPlatform from "../../../api/learningPlatform";

/**
 * Rich text editor for the learning platform.
 *
 * - Built on react-quill-new (already a transitive dependency of the project).
 * - Image insertion uploads to S3 via the dashboard `/media` endpoint and
 *   embeds the public URL instead of a base64 blob so the lesson payload
 *   stays small.
 * - Accepts and emits HTML strings.
 */
const RichTextEditor = ({
  value,
  onChange,
  placeholder,
  isDark,
  minHeight = 200,
}) => {
  const { uploadMedia } = useLearningPlatform();
  const quillRef = useRef(null);

  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const editor = quillRef.current?.getEditor?.();
      const range = editor?.getSelection(true);
      // Insert a temporary placeholder while uploading
      const index = range ? range.index : editor?.getLength() || 0;
      try {
        const result = await uploadMedia(file);
        if (result?.url) {
          editor.insertEmbed(index, "image", result.url, "user");
          editor.setSelection(index + 1);
        }
      } catch (e) {
        // surface to console; UI shows the rest of the editor as-is
        console.error("Image upload failed", e);
      }
    };
    input.click();
  }, [uploadMedia]);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: [
          [{ header: [1, 2, 3, false] }],
          ["bold", "italic", "underline", "strike"],
          [{ color: [] }, { background: [] }],
          [{ list: "ordered" }, { list: "bullet" }],
          [{ align: [] }],
          ["link", "image", "video"],
          ["blockquote", "code-block"],
          ["clean"],
        ],
        handlers: { image: imageHandler },
      },
      clipboard: { matchVisual: false },
    }),
    [imageHandler]
  );

  return (
    <div
      className={`quill-wrap rounded-md border overflow-hidden ${
        isDark
          ? "border-white/[0.08] bg-white/[0.02]"
          : "border-gray-200 bg-white"
      }`}
      data-dark={isDark ? "1" : "0"}
    >
      <style>{`
        .quill-wrap .ql-toolbar {
          border: none !important;
          border-bottom: 1px solid ${isDark ? "rgba(255,255,255,0.06)" : "#e5e7eb"} !important;
          background: ${isDark ? "rgba(255,255,255,0.03)" : "#f9fafb"};
        }
        .quill-wrap .ql-container {
          border: none !important;
          font-family: inherit !important;
          font-size: 13px;
          min-height: ${minHeight}px;
          color: ${isDark ? "#e5e7eb" : "#111827"};
          background: transparent;
        }
        .quill-wrap .ql-editor {
          min-height: ${minHeight}px;
          font-family: inherit !important;
          font-size: 13px !important;
          line-height: 1.65 !important;
          color: ${isDark ? "#e5e7eb" : "#111827"};
        }
        .quill-wrap .ql-editor p,
        .quill-wrap .ql-editor li,
        .quill-wrap .ql-editor h1,
        .quill-wrap .ql-editor h2,
        .quill-wrap .ql-editor h3 {
          font-family: inherit !important;
        }
        .quill-wrap .ql-editor.ql-blank::before {
          color: ${isDark ? "#6b7280" : "#9ca3af"};
          font-style: normal;
          font-family: inherit;
        }
        .quill-wrap .ql-snow .ql-stroke {
          stroke: ${isDark ? "#9ca3af" : "#374151"};
        }
        .quill-wrap .ql-snow .ql-fill,
        .quill-wrap .ql-snow .ql-stroke.ql-fill {
          fill: ${isDark ? "#9ca3af" : "#374151"};
        }
        .quill-wrap .ql-picker-label {
          color: ${isDark ? "#9ca3af" : "#374151"};
        }
        .quill-wrap .ql-picker-options {
          background: ${isDark ? "#1f2937" : "#fff"};
          color: ${isDark ? "#e5e7eb" : "#111827"};
        }
        .quill-wrap .ql-tooltip {
          background: ${isDark ? "#1f2937" : "#fff"};
          color: ${isDark ? "#e5e7eb" : "#111827"};
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
      `}</style>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        modules={modules}
      />
    </div>
  );
};

export default RichTextEditor;
